import { Injectable, NgZone, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Platform } from '@ionic/angular';
import { Logger, LoggingService } from 'ionic-logging-service';
import { PlatformService } from '@app/services/platform/platform.service';
import { AuthService } from '@app/services/auth/auth.service';
import { SanitizationService } from '@app/services/sanitization/sanitization.service';

declare const cordova: any;

@Injectable({ providedIn: 'root' })
export class ShareExtensionService {

  // ############### SIGNALS ###############

  // Full path to the PDF in Documents/ImportedShareExtension/ while a share is pending.
  // Accessible from all pages — equivalent to $rootScope in AngularJS.
  // Set by checkPendingShare(); cleared by clearPendingFilePath() once consumed.
  public pendingFilePath = signal<string | null>(null);

  // True from the moment the Share Extension delivers a file until the user
  // navigates away from DocumentPrintPage (lets other pages show a loading indicator
  // or block certain actions while the share is in flight).
  public pendingShareIntent = signal<boolean>(false);

  // Filename preserved for deleteSharedFile() after upload; not cleared with pendingFilePath.
  private pendingFilename = signal<string | null>(null);

  // Set to true before the native getPendingFilePath call to block concurrent triggers
  // (both platform.ready and handleShareURL can fire at nearly the same time).
  // Stays true until deleteSharedFile() removes the file from disk — this prevents
  // platform.resume from re-triggering the share flow while the user is still on
  // DocumentPrintPage and the App Group file hasn't been deleted yet.
  private pendingShareInProgress = false;

  // ############### INIT ###############

  private logger: Logger;

  constructor(
    private authService: AuthService,
    private loggingService: LoggingService,
    private ngZone: NgZone,
    private platform: Platform,
    private platformService: PlatformService,
    private router: Router,
    private sanitizationService: SanitizationService
  ) {
    this.logger = this.loggingService.getLogger('[ShareExtensionService]');
    const methodName = 'ctor';
    this.logger.entry(methodName);

    // Cold-start: check for a pending share once the Cordova bridge is ready.
    // Warm-start: re-check every time the app is foregrounded (covers the case
    // where the Share Extension opens an already-running app and window.handleOpenURL
    // fires before Angular is bootstrapped).
    this.platform.ready().then(() => {
      if (this.platformService.isCordova() && this.platformService.isIOS()) {
        this.checkPendingShare('platform.ready');

        this.platform.resume.subscribe(() => {
          this.checkPendingShare('resume');
        });
      }
    });
  }

  // ############### PUBLIC API ###############

  // Called by AppComponent when a printixapp://share URL is detected via
  // window.handleOpenURL or the deeplinks plugin. The URL is only a wake-up
  // signal — the actual file is read from the App Group container.
  public handleShareURL(url: string): void {
    this.logger.info('ShareExtension: handleShareURL received');
    if (!this.platformService.isCordova() || !this.platformService.isIOS()) { return; }
    this.checkPendingShare('handleShareURL');
  }

  // Clears pendingFilePath once the DocumentPrintPage has consumed it.
  // Does NOT clear pendingFilename — that is kept until deleteSharedFile() is called.
  public clearPendingFilePath(): void {
    this.pendingFilePath.set(null);
    this.pendingShareIntent.set(false);
    // Do NOT reset pendingShareInProgress here — the file still exists in the
    // App Group container until deleteSharedFile() is called. Keeping the flag
    // true blocks platform.resume from re-triggering the share flow while the
    // user is still on DocumentPrintPage.
  }

  // Called when readFileAsJSFile fails before the upload flow starts.
  // Clears pending intent and releases the in-progress lock so a new share
  // can be processed without requiring a page leave/enter cycle.
  public resetPendingShareAfterReadFailure(): void {
    this.pendingFilePath.set(null);
    this.pendingShareIntent.set(false);
    this.pendingShareInProgress = false;
  }

  // Reads a PDF file by name and returns a JS File object (base64 → Blob → File).
  // Rejects after 10 s if the native bridge does not respond (plugin crash / timeout).
  public readFileAsJSFile(filename: string): Promise<File> {
    const cordovaCall = new Promise<File>((resolve, reject) => {
      cordova.plugins.shareExtension.readSharedFile(
        filename,
        (res: { base64: string; mimeType: string }) => {
          try {
            const arrayBuffer = this.base64ToArrayBuffer(res.base64);
            // WKWebView on iOS does not propagate `name` through the File constructor —
            // attach name/lastModified directly to the Blob instead.
            const blob  = new Blob([arrayBuffer as unknown as BlobPart], { type: res.mimeType });
            const file  = Object.assign(blob, { name: filename, lastModified: Date.now() }) as unknown as File;
            this.logger.info('ShareExtension: file loaded name=' + file.name + ' size=' + file.size);
            resolve(file);
          } catch (err) {
            this.logger.error('readFileAsJSFile', 'ShareExtension: FAILED creating File — ' + String(err));
            reject(new Error('Step 20: ' + String(err)));
          }
        },
        (err: any) => {
          this.logger.error('readFileAsJSFile', 'ShareExtension: readSharedFile FAILED — ' + String(err));
          reject(new Error('Step 19: ' + String(err)));
        }
      );
    });

    const timeout = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('Step 18: native bridge timeout after 10s')), 10_000)
    );

    return Promise.race([cordovaCall, timeout]);
  }

  // Deletes the shared file from both the App Group container and Documents.
  // Call after a successful upload.
  public deleteSharedFile(): void {
    const filename = this.pendingFilename();
    this.pendingFilename.set(null);
    // File is being removed from disk — allow the next share intent to be processed.
    this.pendingShareInProgress = false;

    if (!filename || !this.platformService.isCordova() || !this.platformService.isIOS()) {
      return;
    }

    new Promise<void>((resolve, reject) => {
      cordova.plugins.shareExtension.deleteSharedFile(filename, resolve, reject);
    }).catch(err => {
      this.logger.error('deleteSharedFile', 'ShareExtension: deleteSharedFile FAILED (non-fatal) — ' + JSON.stringify(err));
    });
  }

  // ############### PRIVATE ###############

  private checkPendingShare(trigger: string): void {
    if (this.pendingShareInProgress) {
      return;
    }
    this.pendingShareInProgress = true;

    // Guard against a native bridge hang: if neither callback fires within 5 s,
    // reset the lock so that future resume/handleShareURL triggers can retry.
    const bridgeTimeoutId = setTimeout(() => {
      if (this.pendingShareInProgress && !this.pendingFilePath()) {
        this.pendingShareInProgress = false;
        this.logger.warn('checkPendingShare', 'ShareExtension: getPendingFilePath timed out after 5s — lock released');
      }
    }, 5_000);

    cordova.plugins.shareExtension.getPendingFilePath(
      (filePath: string | null) => {
        clearTimeout(bridgeTimeoutId);
        if (!filePath) {
          this.pendingShareInProgress = false;
          return;
        }

        const filename = filePath.split('/').pop() ?? filePath;
        this.ngZone.run(() => {
          if (this.pendingFilePath()) {
            return;
          }
          this.logger.info('ShareExtension: pending file detected filename=' + this.sanitizationService.sanitizeLogOutput(filename) + ' trigger=' + trigger);
          this.pendingFilePath.set(filePath);
          this.pendingFilename.set(filename);
          this.pendingShareIntent.set(true);
          this.navigateToDocumentPrintIfAuthenticated();
        });
      },
      (err: any) => {
        clearTimeout(bridgeTimeoutId);
        this.pendingShareInProgress = false;
        this.logger.error('checkPendingShare', 'ShareExtension: getPendingFilePath FAILED — ' + JSON.stringify(err));
      }
    );
  }

  private navigateToDocumentPrintIfAuthenticated(): void {
    const methodName = 'navigateToDocumentPrintIfAuthenticated';

    if (!this.authService.isAuthenticatedSignal()) {
      return;
    }

    if (this.router.url.startsWith('/document-print')) {
      return;
    }

    // On cold-start (user not authenticated), auth pages read pendingShareIntent()
    // and navigate to /document-print themselves. On warm-start (user already
    // authenticated), we can navigate directly from any route without listing them.
    this.logger.info('ShareExtension: navigating to /document-print from ' + this.router.url);
    this.router.navigate(['/document-print']).then(() => {
      this.logger.info('ShareExtension: navigated to /document-print');
    }).catch(err => {
      this.logger.error(methodName, 'ShareExtension: navigate FAILED — ' + JSON.stringify(err));
    });
  }

  private base64ToArrayBuffer(base64: string): ArrayBuffer {
    const binary = atob(base64);
    const buffer = new ArrayBuffer(binary.length);
    const bytes  = new Uint8Array(buffer);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return buffer;
  }
}
